import ExpandMore from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material";
import Autocomplete, { autocompleteClasses, createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Popper from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProgressContext } from "../../../context/ProgressProvider";
import { StepperContext } from "../../../context/StepperProvider";
import { VehicleContext, IVehicleContext } from "../../../context/VehicleProvider";
import { Vehicle } from "../../../data/vehicle";
import { useVehicleByIdQuery, useVehiclesQuery } from "../../../hooks/useVehiclesQuery";
import ListBox from "./ListBox";

export default function AutocompleteVirtualized() {
    const { vehicle, clearVehicle, setVehicle } = useContext(VehicleContext) as IVehicleContext;
    const { setIsLoading } = useContext(ProgressContext)
    const [autocompleteOpen, setAutocompleteOpen] = useState(false)
    const [vehicles, setVehicles] = useState<Vehicle[]>([])
    const { setNextDisabled } = useContext(StepperContext)
    const vehiclesQuery = useVehiclesQuery()
    const { t } = useTranslation()
    const [userVehicle, setUserVehicle] = useState<Vehicle>({} as Vehicle)
    const [refetchVehicleDetails, setRefetchVehicleDetails] = useState(false)
    const vehicleDetails = useVehicleByIdQuery(vehicle?.Id, refetchVehicleDetails)
    const filterOptions = createFilterOptions({
        stringify: (option: Vehicle) => `${option.Brand} ${option.Model} ${option.Version} ` + `${option.Brand} ${option.Model} ${option.Version}`.replace("-", ' '),
    });

    useEffect(() => {
        if (vehiclesQuery.isSuccess) {
            if (vehiclesQuery.error) {
                setNextDisabled(true)
                return
            }

            if (vehiclesQuery.data) {
                let result: Vehicle[] = [];
                const map = new Map();
                for (const item of vehiclesQuery.data) {
                    if (!map.has(item.Brand + item.Model + item.Version)) {
                        map.set(item.Brand + item.Model + item.Version, true)
                        result.push(item)
                    }
                }
                setVehicles(result)
                setAutocompleteOpen(true)
            }
        }
    }, [vehiclesQuery.isSuccess])

    useEffect(() => {
        if (vehicleDetails.isSuccess) {
            if (vehicleDetails.error) {
                setNextDisabled(true)
            }

            if (Object.keys(userVehicle).length === 0 && vehicle) {
                setUserVehicle(vehicle)
            }

            setAutocompleteOpen(false)
            setIsLoading(false)
        }
    }, [vehicleDetails.isSuccess, vehicle])

    const onSelectVehicle = (selectedVehicle: Vehicle | null, reason: string) => {
        switch (reason) {
            case "selectOption":
                setIsLoading(true)
                clearVehicle()
                if (selectedVehicle) {
                    setUserVehicle(selectedVehicle)
                    setVehicle(selectedVehicle)
                    setRefetchVehicleDetails(true)
                }
                break
            case "clear":
            case "removeOption":
                clearVehicle()
                setUserVehicle({} as Vehicle)
                break
            default:
                break;
        }
    }

    const StyledPopper = styled(Popper)({
        [`& .${autocompleteClasses.listbox}`]: {
            boxSizing: 'border-box',
            '& li': {
                padding: 0,
                margin: ".5rem 0 .5rem 0 !important",
            },
        },
    });

    return (
        <Autocomplete
            id="vehicle-models-input"
            selectOnFocus
            clearOnBlur={true}
            options={[...vehicles, {} as Vehicle]}
            groupBy={((option: Vehicle) => (option.Brand || "").toUpperCase())}
            getOptionLabel={(option: Vehicle) => `${(option.Brand ? option.Brand : "")}${(option.Model ? (" - " + option.Model) : "")}${(option.Version ? (" - " + option.Version) : "")}`}
            value={userVehicle}
            isOptionEqualToValue={(option: Vehicle, value: Vehicle) => (option.Brand === value.Brand && option.Model === value.Model && option.Version === value.Version)}
            loading={vehiclesQuery.isLoading}
            loadingText={t("vehicle-profile.loading")}
            noOptionsText={t("vehicle-profile.no-options")}
            open={autocompleteOpen}
            onOpen={() => setAutocompleteOpen(prevState => !prevState)}
            onClose={() => setAutocompleteOpen(prevState => !prevState)}
            onChange={(_event: any, value: Vehicle | null, reason: string) => onSelectVehicle(value, reason)}
            filterOptions={filterOptions}
            disabled={vehiclesQuery.isLoading}
            PopperComponent={StyledPopper}
            ListboxComponent={ListBox}
            popupIcon={<ExpandMore></ExpandMore>}
            renderInput={(params) => (
                <div className='autocomplete'>
                    <TextField {...params}
                        autoFocus
                        label={(vehiclesQuery.isLoading ? t("vehicle-profile.loading") : t("vehicle-profile.input-label"))}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (vehiclesQuery.isLoading ? <CircularProgress color="secondary" size={20} /> : params.InputProps.endAdornment)
                        }} />
                </div>
            )}
            renderOption={(props, option, { selected }) => [props, option, selected] as React.ReactNode}
            renderGroup={(params) => params as unknown as React.ReactNode}
        />
    )
}